import React, { createContext, useContext, useReducer, useEffect, useMemo, useRef } from 'react';
import {
  authenticatePin,
  getRoomInventory,
  startWorkTask,
  pauseWorkTask,
  finishWorkTask,
  resumeWorkTask,
  resetWorkTask,
  handleManufacturingError
} from '../services/manufacturing'; // Updated import path
import { getCurrentTimestamp } from '../utils/dateUtils';

// Initial state
const initialState = {
  // User & Authentication
  user: null,
  isAuthenticated: false,
  userJobTitle: null,
  userLocation: null,
  
  // Room & Session
  currentRoom: null,
  sessionActive: false,
  lastActivity: null,
  
  // Room Data
  roomInventory: [],
  selectedTask: null,

    // Search State
  searchResults: null,
  isShowingSearchResults: false,
  lastSearchTerm: null,

  // UI State
  loading: false,
  error: null,
  success: null,
  
  // Hold/Pause State
  holdModal: {
    isOpen: false,
    taskId: null,
    reason: '',
  },
  
  // System State
  isOnline: navigator.onLine,
  lastSync: null,
};

// Action types
const actionTypes = {
  // Authentication
  SET_USER: 'SET_USER',
  LOGOUT: 'LOGOUT',
  
  // Room Management
  SET_CURRENT_ROOM: 'SET_CURRENT_ROOM',
  SET_ROOM_INVENTORY: 'SET_ROOM_INVENTORY',
  UPDATE_ROOM_INVENTORY: 'UPDATE_ROOM_INVENTORY',
  SET_SELECTED_TASK: 'SET_SELECTED_TASK',
  CLEAR_SELECTED_TASK: 'CLEAR_SELECTED_TASK',
  
    // Search Management
  SET_SEARCH_RESULTS: 'SET_SEARCH_RESULTS',
  CLEAR_SEARCH_RESULTS: 'CLEAR_SEARCH_RESULTS',

  // UI State
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_SUCCESS: 'SET_SUCCESS',
  CLEAR_MESSAGES: 'CLEAR_MESSAGES',
  
  // Modal State
  OPEN_HOLD_MODAL: 'OPEN_HOLD_MODAL',
  CLOSE_HOLD_MODAL: 'CLOSE_HOLD_MODAL',
  
  // System State
  SET_ONLINE_STATUS: 'SET_ONLINE_STATUS',
  UPDATE_LAST_SYNC: 'UPDATE_LAST_SYNC',
  
  // Session Management
  UPDATE_LAST_ACTIVITY: 'UPDATE_LAST_ACTIVITY',
};

// Reducer function
const manufacturingReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_USER:
      return {
        ...state,
        user: action.payload.user,
        userJobTitle: action.payload.userJobTitle,
        userLocation: action.payload.userLocation,
        isAuthenticated: true,
        sessionActive: true,
        lastActivity: new Date(),
      };

    case actionTypes.LOGOUT:
      return {
        ...initialState,
        isOnline: state.isOnline,
        currentRoom: state.currentRoom, // Keep room when logging out
      };

    case actionTypes.SET_CURRENT_ROOM:
      return {
        ...state,
        currentRoom: action.payload,
        selectedTask: null,
      };

    case actionTypes.SET_ROOM_INVENTORY:
      return {
        ...state,
        roomInventory: action.payload,
      };

    case actionTypes.UPDATE_ROOM_INVENTORY:
      return {
        ...state,
        roomInventory: state.roomInventory.map(item => 
          item.id === action.payload.id ? { ...item, ...action.payload.updates } : item
        ),
      };

    case actionTypes.SET_SELECTED_TASK:
      return {
        ...state,
        selectedTask: action.payload,
        lastActivity: new Date(),
      };

    case actionTypes.CLEAR_SELECTED_TASK:
      return {
        ...state,
        selectedTask: null,
      };

    case actionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case actionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };

    case actionTypes.SET_SUCCESS:
      return {
        ...state,
        success: action.payload,
        loading: false,
      };
      case actionTypes.SET_SEARCH_RESULTS:
        return {
          ...state,
          searchResults: action.payload.results,
          isShowingSearchResults: true,
          lastSearchTerm: action.payload.searchTerm,
        };

      case actionTypes.CLEAR_SEARCH_RESULTS:
        return {
          ...state,
          searchResults: null,
          isShowingSearchResults: false,
          lastSearchTerm: null,
        };

    case actionTypes.CLEAR_MESSAGES:
      return {
        ...state,
        error: null,
        success: null,
      };

    case actionTypes.OPEN_HOLD_MODAL:
      return {
        ...state,
        holdModal: {
          isOpen: true,
          taskId: action.payload.taskId,
          reason: '',
        },
      };

    case actionTypes.CLOSE_HOLD_MODAL:
      return {
        ...state,
        holdModal: {
          isOpen: false,
          taskId: null,
          reason: '',
        },
      };

    case actionTypes.SET_ONLINE_STATUS:
      return {
        ...state,
        isOnline: action.payload,
      };

    case actionTypes.UPDATE_LAST_SYNC:
      return {
        ...state,
        lastSync: new Date(),
      };

    case actionTypes.UPDATE_LAST_ACTIVITY:
      return {
        ...state,
        lastActivity: new Date(),
      };

    default:
      return state;
  }
};

// Create context
const ManufacturingContext = createContext();

// Custom hook to use the context
export const useManufacturingContext = () => {
  const context = useContext(ManufacturingContext);
  if (!context) {
    throw new Error('useManufacturingContext must be used within ManufacturingProvider');
  }
  return context;
};

// Context provider component
export const ManufacturingProvider = ({ children }) => {
  const [state, dispatch] = useReducer(manufacturingReducer, initialState);
  const isFetchingInventoryRef = useRef(false);

  // Utility functions for dispatching actions - memoized since they only
  // close over the stable `dispatch` function, so this object never needs to be recreated
  const actions = useMemo(() => ({
    // Authentication
    setUser: (user, userJobTitle, userLocation) => {
      dispatch({
        type: actionTypes.SET_USER,
        payload: { user, userJobTitle, userLocation },
      });
    },

    logout: () => {
      dispatch({ type: actionTypes.LOGOUT });
    },
    // Search Management
      setSearchResults: (results, searchTerm) => {
        dispatch({
          type: actionTypes.SET_SEARCH_RESULTS,
          payload: { results, searchTerm },
        });
      },

      clearSearchResults: () => {
        dispatch({ type: actionTypes.CLEAR_SEARCH_RESULTS });
      },
    // Room Management
    setCurrentRoom: (roomNo) => {
      dispatch({
        type: actionTypes.SET_CURRENT_ROOM,
        payload: roomNo,
      });
    },

    setRoomInventory: (inventory) => {
      dispatch({
        type: actionTypes.SET_ROOM_INVENTORY,
        payload: inventory,
      });
    },

    updateTaskInInventory: (taskId, updates) => {
      dispatch({
        type: actionTypes.UPDATE_ROOM_INVENTORY,
        payload: { id: taskId, updates },
      });
    },

    selectTask: (task) => {      
      dispatch({
        type: actionTypes.SET_SELECTED_TASK,
        payload: task,
      });
    },
    clearSelectedTask: () => {
      dispatch({ type: actionTypes.CLEAR_SELECTED_TASK });
    },

    // UI State
    setLoading: (loading) => {
      dispatch({
        type: actionTypes.SET_LOADING,
        payload: loading,
      });
    },

    setError: (error, duration = 5000) => {
      dispatch({
        type: actionTypes.SET_ERROR,
        payload: error,
      });
      
      if (duration > 0) {
        setTimeout(() => {
          dispatch({ type: actionTypes.CLEAR_MESSAGES });
        }, duration);
      }
    },

    setSuccess: (success, duration = 3000) => {
      dispatch({
        type: actionTypes.SET_SUCCESS,
        payload: success,
      });
      
      if (duration > 0) {
        setTimeout(() => {
          dispatch({ type: actionTypes.CLEAR_MESSAGES });
        }, duration);
      }
    },

    clearMessages: () => {
      dispatch({ type: actionTypes.CLEAR_MESSAGES });
    },

    // Modal Management
    openHoldModal: (taskId) => {
      dispatch({
        type: actionTypes.OPEN_HOLD_MODAL,
        payload: { taskId },
      });
    },

    closeHoldModal: () => {
      dispatch({ type: actionTypes.CLOSE_HOLD_MODAL });
    },

    // Activity Tracking
    updateActivity: () => {
      dispatch({ type: actionTypes.UPDATE_LAST_ACTIVITY });
    },
  }), []);

  // API Integration Methods - recreated only when `state` changes, not on every render
  const apiMethods = useMemo(() => ({
    // Authentication - simplified to use room number as PIN
    authenticate: async (pinCode) => {
      try {
        actions.setLoading(true);
        actions.clearMessages();

        // Pin code should match the room number
        const roomNo = state.currentRoom || pinCode;
        
        const response = await authenticatePin(pinCode, roomNo);
        
        actions.setUser(
          response.user,
          response.userJobTitle,
          response.userLocation
        );
        
        // Load room inventory immediately after authentication
        await apiMethods.loadRoomInventory();
        // actions.refreshSelectedTask();

        actions.setSuccess('Authentication successful');
        return response;
      } catch (error) {
        actions.setError(handleManufacturingError(error, 'Authentication failed'));
        throw error;
      } finally {
        actions.setLoading(false);
      }
    },
    clearSearchAndShowAll: () => {
      actions.clearSearchResults();
      actions.clearSelectedTask();
      actions.clearMessages();
    },
    // Room Inventory
    loadRoomInventory: async (roomNo = null) => {
      const targetRoom = roomNo || state.currentRoom;
      if (!targetRoom) return;

      try {
        actions.setLoading(true);
        
        const inventory = await getRoomInventory(targetRoom);
        actions.setRoomInventory(inventory);
        
        return inventory;
      } catch (error) {
        actions.setError(handleManufacturingError(error, 'Failed to load room inventory'));
      } finally {
        actions.setLoading(false);
      }
    },

    // Barcode Search
    findBarcode: async (barcode, serialCode = null) => {
      if (!barcode && !serialCode) {
        actions.setError('Barcode or serial code is required');
        return;
      }

      try {
        actions.setLoading(true);
        actions.clearMessages();

        // Search through the locally loaded roomInventory
        const searchTerm = (barcode || serialCode).toLowerCase().trim();
        
        const matchingTasks = state.roomInventory.filter(task => {
          // Search in barcode field
          const barcodeMatch = task.barcode && 
            task.barcode.toLowerCase().includes(searchTerm);
          
          // Search in serial number if provided and available
          const serialMatch = task.serialNo && 
            task.serialNo.toLowerCase().includes(searchTerm);
          
          // Search in order number as well (helpful for partial matches)
          const orderMatch = task.orderNo && 
            task.orderNo.toString().includes(searchTerm);

          return barcodeMatch || serialMatch || orderMatch;
        });
        
        if (matchingTasks.length > 0) {
          // Set search results and select the first match
          actions.setSearchResults(matchingTasks, barcode || serialCode);
          actions.selectTask(matchingTasks[0]);
          actions.setSuccess(`Found ${matchingTasks.length} item(s) matching "${searchTerm}"`);
          return { tasks: matchingTasks, found: true, searchTerm: searchTerm };
        } else {
          // Show empty search results
          actions.setSearchResults([], barcode || serialCode);
          actions.setError(`No items found matching "${searchTerm}"`);
          return { tasks: [], found: false, searchTerm: searchTerm };
        }
      } catch (error) {
        actions.setError('Search failed: ' + error.message);
        return { tasks: [], found: false, searchTerm: barcode || serialCode };
      } finally {
        actions.setLoading(false);
      }
    },

    // Task Operations
    finishWork: async (taskId = null) => {
      const targetTaskId = taskId || state.selectedTask?.id;
      if (!targetTaskId || !state.user?.id) {
        actions.setError('No task or user selected');
        return;
      }

        try {
        actions.setLoading(true);
        
        const result = await finishWorkTask(
          targetTaskId, 
          state.user.id, 
          state.currentRoom
        );
        
        // UPDATE: Add immediate state updates
        actions.updateTaskInInventory(targetTaskId, { 
          status: 'Task Completed',
          scanOutDate: new Date().toLocaleString('sv-SE').replace(' ', 'T'),
          scanOutBy: state.user.fullName
        });

        // UPDATE: Refresh the selected task immediately
        if (state.selectedTask && state.selectedTask.id === targetTaskId) {
          actions.selectTask({
            ...state.selectedTask,
            status: 'Task Completed',
            scanOutDate: new Date().toLocaleString('sv-SE').replace(' ', 'T'),
            scanOutBy: state.user.fullName
          });
        }
        
        actions.setSuccess('Work completed successfully');
        await apiMethods.loadRoomInventory();
        
        return result;
      } catch (error) {
        actions.setError(handleManufacturingError(error, 'Failed to finish work'));
      } finally {
        actions.setLoading(false);
      }
    },
  
    startWork: async (taskId = null, workerId = null) => {
      const targetTaskId = taskId || state.selectedTask?.id;
      const targetWorkerId = workerId || state.user?.id;
      
      if (!targetTaskId || !targetWorkerId) {
        actions.setError('No task or user selected');
        return;
      }

      try {
        actions.setLoading(true);
        
        const result = await startWorkTask(
          targetTaskId, 
          targetWorkerId, 
          state.currentRoom
        );
        
        // Update the inventory
        actions.updateTaskInInventory(targetTaskId, { 
          status: 'In-Process', 
          worker: state.user.fullName,
          scanInDate: new Date().toLocaleString('sv-SE').replace(' ', 'T'), // Add this
          scanInBy: state.user.fullName         // Add this
        });
        
        // IMPORTANT: Update the selected task with new data
        if (state.selectedTask && state.selectedTask.id === targetTaskId) {
          actions.selectTask({
            ...state.selectedTask,
            status: 'In-Process',
            worker: state.user.fullName,
            scanInDate: new Date().toLocaleString('sv-SE').replace(' ', 'T'),
            scanInBy: state.user.fullName
          });
        }
        
        actions.setSuccess('Work started successfully');
        await apiMethods.loadRoomInventory();
        
        return result;
      } catch (error) {
        actions.setError(handleManufacturingError(error, 'Failed to start work'));
      } finally {
        actions.setLoading(false);
      }
    },

    pauseTask: async (reason, taskId = null) => {
      const targetTaskId = taskId || state.selectedTask?.id;
      if (!targetTaskId || !state.user?.id) {
        actions.setError('No task or user selected');
        return;
      }

      try {
        actions.setLoading(true);
        
        const result = await pauseWorkTask(
          targetTaskId, 
          reason, 
          state.user.id,
          state.currentRoom  // Add room number
        );
        
        // Determine status based on reason
        let status = 'On Hold';
        if (reason.toLowerCase().includes('lunch')) {
          status = 'Lunch Break';
        }
        if (state.selectedTask && state.selectedTask.id === targetTaskId) {
  actions.selectTask({
    ...state.selectedTask,
    status,
    isWorkPaused: true,
    holdReason: reason,
    onHoldBy: state.user.fullName, // Use fullName for consistency
    onHoldStartDate: new Date().toLocaleString('sv-SE').replace(' ', 'T')
  });
}
        actions.updateTaskInInventory(targetTaskId, { 
          status,
          isWorkPaused: true,
          holdReason: reason,
          onHoldBy: state.user.id,
          onHoldStartDate: new Date().toLocaleString('sv-SE').replace(' ', 'T')
        });
        actions.setSuccess(`Task paused: ${reason}`);
        
        // Refresh inventory
        await apiMethods.loadRoomInventory();
        
        return result;
      } catch (error) {
        actions.setError(handleManufacturingError(error, `Failed to pause task`));
      } finally {
        actions.setLoading(false);
      }
    },


    resumeTask: async (taskId = null) => {
      const targetTaskId = taskId || state.selectedTask?.id;
      
      // ADD DEBUGGING HERE:
      console.log('=== RESUME TASK DEBUG ===');
      console.log('taskId parameter:', taskId);
      console.log('selectedTask:', state.selectedTask);
      console.log('selectedTask.id:', state.selectedTask?.id);
      console.log('targetTaskId:', targetTaskId);
      console.log('typeof targetTaskId:', typeof targetTaskId);
      console.log('========================');
      
      if (!targetTaskId || !state.user?.id) {
        actions.setError('No task or user selected');
        return;
      }

      try {
        actions.setLoading(true);
        
        // ENSURE taskId is a number or string, not an object
        const safeTaskId = typeof targetTaskId === 'object' ? targetTaskId.id : targetTaskId;
        
        console.log('safeTaskId:', safeTaskId);
        console.log('typeof safeTaskId:', typeof safeTaskId);
        
        const result = await resumeWorkTask(
          safeTaskId,  // Use the safe task ID
          state.user.id,
          state.currentRoom
        );
        
        actions.updateTaskInInventory(safeTaskId, { 
          status: 'In-Process',
          isWorkPaused: false,
          onHoldEndDate: getCurrentTimestamp()
        });
        // IMPORTANT: Update the selected task if it matches
        if (state.selectedTask && state.selectedTask.id == safeTaskId) {
          actions.selectTask({
            ...state.selectedTask,
            status: 'In-Process',
            isWorkPaused: false,
            onHoldEndDate: getCurrentTimestamp()
          });
        }
        actions.setSuccess('Task resumed successfully');
        
        // Refresh inventory
        await apiMethods.loadRoomInventory();
        
        return result;
      } catch (error) {
        actions.setError(handleManufacturingError(error, 'Failed to resume task'));
      } finally {
        actions.setLoading(false);
      }
    },

  }), [state, actions]);

 // Auto-refresh inventory when room changes
useEffect(() => {
  let interval;
  
  if (state.isAuthenticated && state.currentRoom) {
    // Load inventory immediately
    const loadInventory = async () => {
      if (isFetchingInventoryRef.current) return;
      isFetchingInventoryRef.current = true;
      try {
        const inventory = await getRoomInventory(state.currentRoom);
        actions.setRoomInventory(inventory);
      } catch (error) {
        console.error('Failed to refresh inventory:', error);
      } finally {
        isFetchingInventoryRef.current = false;
      }
    };
    
    // Set up auto-refresh every 10 minutes
    interval = setInterval(loadInventory, 600000);
  }
  
  return () => {
    if (interval) clearInterval(interval);
  };
}, [state.isAuthenticated, state.currentRoom]);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      dispatch({ type: actionTypes.SET_ONLINE_STATUS, payload: true });
    };

    const handleOffline = () => {
      dispatch({ type: actionTypes.SET_ONLINE_STATUS, payload: false });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Activity tracking for session management
useEffect(() => {
  const handleActivity = () => {
    if (state.isAuthenticated) {
      dispatch({ type: actionTypes.UPDATE_LAST_ACTIVITY });
    }
  };

  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
  
  events.forEach(event => {
    document.addEventListener(event, handleActivity, true);
  });

  return () => {
    events.forEach(event => {
      document.removeEventListener(event, handleActivity, true);
    });
  };
}, [state.isAuthenticated]);

  // Context value - memoized so consumers only see a new reference when state actually changes
  const contextValue = useMemo(() => ({
    // State
    ...state,

    // Actions
    ...actions,

    // API Methods
    ...apiMethods,

    // Computed properties
    clearSearchAndShowAll: apiMethods.clearSearchAndShowAll,
    canStartWork: state.selectedTask && ['Not Started', 'Active','', 'Pending'].includes(state.selectedTask.status),
    canFinishWork: !!state.selectedTask, // Allow finish work for any status
    canPause: state.selectedTask && state.selectedTask.status === 'In-Process',
    canResume: state.selectedTask && ['On Hold', 'Lunch Break'].includes(state.selectedTask.status),
    canReset: state.selectedTask && state.userJobTitle === 'Transfer',
    isTaskSelected: !!state.selectedTask,
    holdTask: (reason, taskId) => apiMethods.pauseTask(reason, taskId),
  }), [state, actions, apiMethods]);

  return (
    <ManufacturingContext.Provider value={contextValue}>
      {children}
    </ManufacturingContext.Provider>
  );
};

export default ManufacturingContext;